import React from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  MapPin, 
  Calendar, 
  CheckCircle2, 
  TrendingUp, 
  Clock, 
  ShieldCheck, 
  FileText,
  Download,
  Share2
} from "lucide-react";
import { cn, formatPrice } from "@/lib/utils";

interface ProfessionalReportProps {
  valuation: any;
}

export function ProfessionalReport({ valuation }: ProfessionalReportProps) {
  if (!valuation) return null;

  const { propertyData, results } = valuation;
  const confidenceColor = results.confidenceLevel === 'alta' ? 'text-moss' : results.confidenceLevel === 'baja' ? 'text-red-500' : 'text-orange-500';

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-1000">
      {/* 1. Resumen Ejecutivo (Editorial Header) */}
      <div className="brutalist-border bg-ink text-parchment p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-terracotta/10 rounded-full blur-3xl" />
        
        <div className="flex flex-col md:flex-row justify-between gap-12 relative z-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-terracotta text-parchment px-3 py-1 text-[10px] font-black uppercase tracking-[0.3em]">IA Appraisal Report</span>
              <span className="text-[10px] font-black uppercase tracking-[0.3em] opacity-40">Ref: {valuation._id?.slice(-8).toUpperCase()}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-black uppercase italic leading-[0.85] mb-8">
              {propertyData.title}
            </h1>
            <div className="flex items-center text-xl font-serif italic text-parchment/60">
              <MapPin className="mr-2 h-6 w-6 text-terracotta" />
              <p>{propertyData.address}, {propertyData.city}, Chile</p>
            </div>
          </div>

          <div className="text-right flex flex-col justify-end">
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-terracotta block mb-2">Valor Comercial Sugerido</span>
            <div className="text-6xl md:text-8xl font-display font-black leading-none">
              {results.suggestedSalePrice} <span className="text-3xl opacity-40">UF</span>
            </div>
            <p className="text-xl font-serif italic text-parchment/40 mt-4">
              Aprox. ${formatPrice(results.estimatedValueClp)} CLP
            </p>
          </div>
        </div>
      </div>

      {/* 2. Métricas de Mercado y Confianza */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <Card className="brutalist-border p-8 bg-parchment flex flex-col gap-4">
          <ShieldCheck className={cn("h-8 w-8", confidenceColor)} />
          <div>
            <h4 className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Nivel de Confianza</h4>
            <p className={cn("text-2xl font-display font-black uppercase italic", confidenceColor)}>
              {results.confidenceLevel || 'Media'}
            </p>
          </div>
          <p className="text-[10px] font-serif italic text-ink/60">Basado en densidad de datos en {propertyData.city}.</p>
        </Card>

        <Card className="brutalist-border p-8 bg-parchment flex flex-col gap-4">
          <TrendingUp className="h-8 w-8 text-terracotta" />
          <div>
            <h4 className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Precio por M²</h4>
            <p className="text-2xl font-display font-black uppercase italic">
              {results.pricePerM2Uf} <span className="text-sm">UF/M²</span>
            </p>
          </div>
          <p className="text-[10px] font-serif italic text-ink/60">Promedio sector: {results.breakdown?.base_price_m2 || 45} UF/M²</p>
        </Card>

        <Card className="brutalist-border p-8 bg-parchment flex flex-col gap-4">
          <Clock className="h-8 w-8 text-terracotta" />
          <div>
            <h4 className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Tiempo en Mercado</h4>
            <p className="text-2xl font-display font-black uppercase italic">
              {results.estimatedTimeOnMarket || 45} <span className="text-sm">DÍAS</span>
            </p>
          </div>
          <p className="text-[10px] font-serif italic text-ink/60">Estimación de colocación para venta.</p>
        </Card>
      </div>

      {/* 3. Rangos y Detalle */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-12">
          {/* Rangos de Precio */}
          <div className="brutalist-border bg-white p-12">
            <h3 className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-12">Análisis de Rangos Competitivos</h3>
            
            <div className="space-y-12">
              <div>
                <div className="flex justify-between items-end mb-4">
                  <span className="text-xs font-black uppercase tracking-widest">Rango de Venta</span>
                  <span className="text-sm font-serif italic opacity-60">{results.salePriceRange?.min} - {results.salePriceRange?.max} UF</span>
                </div>
                <div className="h-4 bg-ink/5 w-full relative">
                  <div className="absolute inset-y-0 bg-terracotta/20" style={{ left: '10%', right: '10%' }} />
                  <div className="absolute inset-y-0 w-1 bg-terracotta left-1/2 -translate-x-1/2" />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-end mb-4">
                  <span className="text-xs font-black uppercase tracking-widest">Rango de Arriendo</span>
                  <span className="text-sm font-serif italic opacity-60">{results.rentPriceRange?.min} - {results.rentPriceRange?.max} UF</span>
                </div>
                <div className="h-4 bg-ink/5 w-full relative">
                  <div className="absolute inset-y-0 bg-moss/20" style={{ left: '15%', right: '15%' }} />
                  <div className="absolute inset-y-0 w-1 bg-moss left-1/2 -translate-x-1/2" />
                </div>
              </div>
            </div>
          </div>

          {/* Desglose de Ajustes */}
          <div className="space-y-8">
            <h3 className="text-xs font-black uppercase tracking-[0.5em] text-terracotta">Factores de Ajuste IA</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(results.breakdown?.adjustments || {}).map(([key, value]: [string, any]) => (
                key !== 'total' && (
                  <div key={key} className="flex justify-between items-center p-4 border-b border-ink/10">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-60">
                      {key === 'bedrooms' ? 'Dormitorios' : 
                       key === 'bathrooms' ? 'Baños' : 
                       key === 'parkings' ? 'Estacionamientos' : 
                       key === 'age' ? 'Antigüedad' : 
                       key === 'condition' ? 'Conservación' : key}
                    </span>
                    <span className={cn("text-xs font-black", value > 0 ? "text-moss" : value < 0 ? "text-red-500" : "text-ink")}>
                      {value > 0 ? '+' : ''}{(value * 100).toFixed(0)}%
                    </span>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-8">
          <div className="brutalist-border bg-ink text-parchment p-8">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-terracotta mb-6">Acciones de Informe</h4>
            <div className="space-y-4">
              <Button className="w-full h-14 bg-parchment text-ink hover:bg-terracotta hover:text-parchment transition-all">
                <Download className="mr-2 h-4 w-4" /> DESCARGAR PDF
              </Button>
              <Button variant="outline" className="w-full h-14 border-parchment/20 text-parchment hover:bg-parchment hover:text-ink">
                <Share2 className="mr-2 h-4 w-4" /> COMPARTIR
              </Button>
            </div>
          </div>

          <Card className="brutalist-border p-8 bg-parchment">
            <h4 className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-6">Contexto Sectorial</h4>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-xs font-serif italic">Densidad de Oferta</span>
                <span className="text-sm font-black uppercase">Alta</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-serif italic">Plusvalía 12m</span>
                <span className="text-sm font-black uppercase text-moss">+4.2%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-serif italic">Competitividad</span>
                <span className="text-sm font-black uppercase italic">85/100</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
